/** ***********
*
* <asset.effects.ts v1.0>
*
* Copyright (c) 2022 AIRBUS OPERATIONS
*
* Contributors:
*  NG6C740 [thomas.pereira.external@airbus.com]
*
* All rights reserved.
**************/

import { selectZones$, selectZoneState$ } from 'src/app/store/selectors/zone.selector';
import {
    fetchAllAssets,
    successFetchAllAssets,
    mappingSomeAssetsFamilies,
    createAsset,
    successCreateAssets,
    successUpdateAssets,
    updateAsset,
    forceStatus,
    errorUpdateAssets,
    errorCreateAssets,
    mappingAllAssets,
    associateTrackerToNewAsset,
    successAssociateTrackerToNewAsset,
    errorAssociateTrackerToNewAsset,
    dissociateTrackerFromAsset,
    errorDissociateTrackerFromAsset,
    deleteAsset,
    successDeleteAssets,
    errorDeleteAssets,
    errorFetchAllAssets,
    importAssets,
    successImportAssets,
    errorImportAssets
} from 'src/app/store/actions/asset.actions';
import { Store } from '@ngrx/store';
import { AssetService } from 'src/app/services/asset.service';
import { TranslateService } from '@ngx-translate/core';
import { SnackbarComponent } from './../../components/shared/snackbar/snackbar';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { successAssociateTrackerAsset, successCreateTrackers, successDissociateTrackerAsset } from '../actions/tracker.action';
import { selectTrackers$ } from '../selectors/tracker.selectors';
import { CacheService } from 'src/app/services/cache.service';
import { combineLatest, from, Observable, of, Subject } from 'rxjs';
import { catchError, filter, map, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import { ErrorTranslationService } from 'src/app/services/error-translation.service';
import { selectFamilyState$ } from '../selectors/family.selectors';
import { Asset, AssetStatus } from 'src/app/models/asset';
import { selectAssetsArray$, selectAssetsById$ } from '../selectors/asset.selectors';
import { updateAssetCount } from '../actions/profile.action';
import { contextIsValid$, selectUserSelectedContext$ } from '../selectors/filter-context.selectors';
import { noop } from '../actions/_shared.action';
import { successDeleteFamilies } from '../actions/family.action';
import { ObjectStore } from 'src/app/models/cache';
import { LoggerService } from 'src/app/services/logger.service';

export type EffectResult = 'success' | 'error';
@Injectable()
export class AssetEffects {

    // Delta Asset effects
    // fetchAllAssets$ = createEffect(() => {
    //     return this._actions$.pipe(
    //         ofType(fetchAllAssets),
    //         this._cacheService.resetDatabase(false, ObjectStore.Assets),
    //         switchMap(() => this._cacheService.retrieveCacheStatus$()),
    //         map(status => status.assets?.lastRefresh),
    //         switchMap(lastRefresh => this._assetService.getAssets(lastRefresh)),
    //         switchMap(assets => combineLatest([
    //             this._cacheService.deleteData$(ObjectStore.Assets, ...(assets.deletedData ?? []).map(({ id }) => id)),
    //             this._cacheService.updateData$(ObjectStore.Assets, ...(assets.updatedData ?? [])),
    //             this._cacheService.createData$(ObjectStore.Assets, ...(assets.createdData?? [])),
    //         ])),
    //         switchMap(() => this._cacheService.getData$(ObjectStore.Assets)),
    //         map(assets => successFetchAllAssets({ assets })),
    //         catchError((error) => {
    //             this.logger.error('AssetEffect', 'Cannot fetch the assets', error);
    //             return of(errorFetchAllAssets());
    //         }),
    //     );
    // });

    fetchAllAssets$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(fetchAllAssets),
            switchMap(() => 
                from(this._cacheService.resetDatabase(false, ObjectStore.Assets)).pipe(
                    switchMap(() => this._cacheService.retrieveCacheStatus$()),
                    map(status => status.assets?.lastRefresh),
                    switchMap(lastRefresh => this._assetService.getAssets(lastRefresh)),
                    switchMap(assets => combineLatest([
                        this._cacheService.deleteData$(ObjectStore.Assets, ...(assets.deletedData ?? []).map(({ id }) => id)),
                        this._cacheService.updateData$(ObjectStore.Assets, ...(assets.updatedData ?? [])),
                        this._cacheService.createData$(ObjectStore.Assets, ...(assets.createdData ?? [])),
                    ])),
                    switchMap(() => this._cacheService.getData$(ObjectStore.Assets)),
                    map(assets => successFetchAllAssets({ assets })),
                    catchError((error) => {
                        this.logger.error('AssetEffect', 'Cannot fetch the assets', error);
                        return of(errorFetchAllAssets());
                    })
                )
            )
        );
    });
    

    public successFetchAllAssets$ = createEffect(() => { return this._actions$.pipe(
        ofType(successFetchAllAssets),
        switchMap((action) => combineLatest([
          this.store.select(selectFamilyState$).pipe(
            filter(state => state.loaded),
            take(1),
            map(({ data }) => data.families),
          ),
          this.store.select(selectZoneState$).pipe(
            filter(({ loaded }) => loaded),
            map(({ data }) => data),
          )]).pipe(
            map(([families, zones]) => mappingAllAssets({ assets: action.assets, families, zones }))
          )
        ),
    ) });

    public createAsset$ = createEffect(() => { return this._actions$.pipe(
        ofType(createAsset),
        switchMap((action) => this._assetService.createAsset(action.asset).pipe(
            map((createAssetRes) => {
                const text = this._translate.instant('BANNER_SUCCESS_CREATE', { item: createAssetRes.asset.name });
                this._snackBar.open(text, 'green-snackbar', 5000);
                this.effectSubject.next('success');
                if (createAssetRes.tracker && createAssetRes.tracker.id) {
                    this.store.dispatch(successCreateAssets({ payload: [{ ...createAssetRes.asset, trackerId: createAssetRes.tracker.id, tracker: createAssetRes.tracker }] }));
                    this.store.dispatch(successCreateTrackers({ payload: [createAssetRes.tracker] }));
                    return successAssociateTrackerAsset({
                        assetId: createAssetRes.asset.id,
                        trackerId: createAssetRes.tracker.id,
                    });
                } else {
                    return successCreateAssets({ payload: [{ ...createAssetRes.asset, trackerId: createAssetRes.tracker?.id, tracker: createAssetRes.tracker }] });
                }
            }),
            catchError((error) => {
                this._errorTranslationService.handleError(error, 'BANNER_FAIL_INTERNAL_CREATE');
                this.effectSubject.next('error');
                return of(errorCreateAssets());
            })
        )),
    ) });

    public successCreateAssets$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(successCreateAssets),
            switchMap(({ payload }) => this.isAssetInContext$(payload[0]).pipe(
                map(inContext => {
                    if (!inContext) {
                        const text = this._translate.instant('CONTEXT_FILTERED_BY__UPDATE_FOR_VALUE', {
                            type: this._translate.instant('PROVIDER')?.toLowerCase(),
                        });
                        this._snackBar.open(text, 'orange-snackbar', 5000);
                        return noop();
                    }
                    return updateAssetCount({ added: 1 });
                })
            )),
        );
    });

    public updateAssets$ = createEffect(() => { return this._actions$.pipe(
        ofType(updateAsset),
        // select the old version of the asset from the store in order to handle the case of family change (old family id is needed to reach the API pendpoint)
        switchMap(action => combineLatest([of(action), this.store.select(selectAssetsById$(action.asset.id))]).pipe(take(1))),
        switchMap(([action, oldAsset]) => this._assetService.updateAsset(action.asset, oldAsset).pipe(
            map((newAsset) => {
                const text: string = this._translate.instant('BANNER_SUCCESS_EDIT', { value: action.asset.name });
                this._snackBar.open(text, 'green-snackbar', 5000);
                this.effectSubject.next('success');
                return successUpdateAssets({ payload: [newAsset] });
            }),
            catchError((error) => {
                const msgCode = this._errorTranslationService.getMessageCode(error);
                switch (msgCode) {
                    case `ASSET_PN_NOT_IN_FAMILY`:
                        this._errorTranslationService.openSnackBarErrorWithParams('BANNER_FAIL_UPDATE_ASSET_PN', { familyName: oldAsset.family_name, pn: oldAsset.pn });
                        break;
                    case 'UNAUTHORIZED_FAMILY':
                        this._errorTranslationService.openSnackBarErrorWithParams('BANNER_FAIL_ASSET_RIGHTS', { familyName: oldAsset.family.name });
                        break;
                    default:
                        this._errorTranslationService.handleError(error, 'BANNER_FAIL_EDIT');
                        break;
                }
                this.effectSubject.next('error');
                return of(errorUpdateAssets());
            }))),
    ) });

    public successCreateOrUpdateAssets$ = createEffect(() =>
        { return this._actions$.pipe(
            ofType(successCreateAssets, successUpdateAssets),
            switchMap((action) =>
                combineLatest([
                    this.store.select(selectFamilyState$),
                    this.store.select(selectTrackers$).pipe(take(1)),
                    this.store.select(selectZones$).pipe(take(1))
                ]).pipe(
                    filter(([familyState]) => familyState.loaded),
                    take(1),
                    switchMap(([ familyState, trackers, zones ]) => this.isAssetInContext$(action.payload[0]).pipe(
                        map(inContext => {
                            if (inContext) {
                                // update children
                                return mappingSomeAssetsFamilies({
                                    assets: action.payload,
                                    families: familyState.data.families,
                                    trackers: trackers.trackers,
                                    zones,
                                });
                            }
                            return noop();
                        })
                    )),
                ))
        ) });

    public deleteAsset$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(deleteAsset),
            switchMap(({ asset }) => this._assetService.deleteAsset(asset.id, asset.familyId).pipe(
                map(() => {
                    return successDeleteAssets({ payload: [{ id: asset.id }] });
                }),
                catchError((error) => {
                    const msgCode = this._errorTranslationService.getMessageCode(error);
                    let text: string;
                    switch (msgCode) {
                        case `ASSET_NOT_EMPTY`:
                          text = this._translate.instant('BANNER_FAIL_ASSETS_NOT_EMPTY', { asset: asset.name });
                          break;
                        case `ASSET_ALREADY_LOADED`:
                          text = this._translate.instant('BANNER_FAIL_ASSET_LOADED_IN', { asset: asset.name });
                          break;
                        case `ASSET_DELETED`:
                          text = this._translate.instant('ASSET_DELETED');
                          break;
                        default:
                          text = this._translate.instant('BANNER_FAIL_INTERNAL_DELETE');
                          break;
                      }
                      this._errorTranslationService.handleError(error, text);
                      return of(errorDeleteAssets);
                }),
            ))
        )
    });

    public successDeleteAssets$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(successDeleteAssets),
            tap(() => {
                this.effectSubject.next('success');
            }),
            map(({ payload }) => updateAssetCount({ removed: payload.length })),
        );
    });

    public forceStatus$ = createEffect(() => { return this._actions$.pipe(
        ofType(forceStatus),
        switchMap(action => this._assetService.forceStatus(action.payload, action.asset).pipe(
            map(() => {
                const text: string = this._translate.instant('BANNER_SUCCESS_EDIT', { value: action.asset.name });
                this._snackBar.open(text, 'green-snackbar', 5000);
                this.effectSubject.next('success');
                if (action.asset.specificData) {
                    action.asset.specificData.isStatusForced = action.payload.isStatusForced;
                } else {
                    action.asset.specificData = {
                        isStatusForced: action.payload.isStatusForced
                    };
                }
                action.asset.status = action.payload.status as AssetStatus;
                return successUpdateAssets({ payload: [action.asset] });
            }),
            catchError((err) => {
                this._errorTranslationService.handleError(err, 'BANNER_FAIL_EDIT');
                return of(errorUpdateAssets());
            })
        ))
    ) });

    public associateTrackerToNewAsset$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(associateTrackerToNewAsset),
            switchMap(action => this._assetService.createAsset(action.asset).pipe(
                map(createdAsset => {
                    this.effectSubject.next('success');
                    return successAssociateTrackerToNewAsset(createdAsset);
                }),
                catchError((error) => {
                    this.effectSubject.next('error');
                    const msgCode = this._errorTranslationService.getMessageCode(error);
                    if (msgCode === 'UNAUTHORIZED_FAMILY') {
                        this._errorTranslationService.openSnackBarErrorWithParams(msgCode, { familyName: action.asset.name });
                    } else {
                        this._errorTranslationService.handleError(error, 'BANNER_FAIL_INTERNAL_CREATE');
                    }
                    return of(errorAssociateTrackerToNewAsset);
                }),
            )),
        );
    });

    public successAssociateTrackerToNewAsset$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(successAssociateTrackerToNewAsset),
            map(({ asset, tracker }) => {
                if (tracker?.id) {
                    const text = this._translate.instant('BANNER_QUICK_LINK_SUCCESS', { assetName: asset.name, deviceSn: tracker.serial });
                    this._snackBar.open(text, 'green-snackbar', 5000);
                } else {
                    const text = this._translate.instant('BANNER_QUICK_LINK_SEMI_SUCCESS', { assetName: asset.name, deviceSn: tracker.serial });
                    this._snackBar.open(text, 'orange-snackbar', 5000);
                }
                return successCreateAssets({
                    payload: [{
                        ...asset,
                        trackerId: tracker?.id,
                        tracker: tracker,
                    }],
                });
            }),
        );
    });

    public dissociateTrackerFromAsset$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(dissociateTrackerFromAsset),
            switchMap(({ asset, tracker }) =>
                this._assetService.dissociateAssetToTracker(asset.id, tracker.id, asset.familyId).pipe(
            map(() => {
                this.effectSubject.next('success');
                const text = this._translate.instant('BANNER_SUCCESS_ASSOCIATION_REMOVE', {
                    assetName: asset.name,
                    trackerAssetSerial: tracker.serial,
                });
                this._snackBar.open(text, 'green-snackbar', 5000);
                return successDissociateTrackerAsset({ trackerId: tracker.id, assetId: asset.id });
            }),
            catchError(error => {
                this.effectSubject.next('error');
                this._errorTranslationService.handleError(error, 'BANNER_FAIL_EDIT');
                return of(errorDissociateTrackerFromAsset)
            })
        )
            )
        );
    });

    public effectSubject: Subject<EffectResult>;

    /**
     * Check if an asset is included in the selected context
     */
    public isAssetInContext$(asset: Asset): Observable<boolean> {
        return combineLatest([
            this.store.select(contextIsValid$),
            this.store.select(selectUserSelectedContext$),
        ]).pipe(
            take(1),
            // Check that the context is valid
            filter(([ valid ]) => valid),
            // Check if the asset has to be created in the store
            map(([, context]) => {
                if (!context?.providers?.length) {
                    // No filtering done on providers => asset can be created
                    return true;
                }
                // Check if the provider of the asset is within the providers selection
                return context?.providers.includes(asset.tracker?.provider);
            }),
        );
    }

    public removeAssetsByFamilyId$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(successDeleteFamilies),
            withLatestFrom(this.store.select(selectAssetsArray$)),
            map(([action, assets]) => {
                const deleteAssetList = assets.filter(asset =>
                    action.payload.some(family => asset.familyId === family.id)
                );

                return deleteAssetList.map(asset => ({ id: asset.id }));
            }),
            map((deleteAssetIds) => successDeleteAssets({ payload: deleteAssetIds })),
            catchError(() => of(errorDeleteAssets()))
        )
    });

    // Import asset through csv file

    public importAssets$ = createEffect(() => { return this._actions$.pipe(
        ofType(importAssets),
        switchMap((action) => this._assetService.createAssets(action.asset).pipe(
            map((createAssetRes) => {
                const text = this._translate.instant('BANNER_IMPORT_ASSETS');
                this._snackBar.open(text, 'green-snackbar', 5000);
                this.effectSubject.next('success');
                return (successImportAssets());
            }),
            catchError((error) => { 
                const errors = JSON.parse(error?.error?.message);               
                const text = (errors?.messageCode === 'INVALID_INPUT') ? this._translate.instant('INVALID_CSV_INPUT') : this._translate.instant('BANNER_FAIL_INTERNAL_CREATE');
                this._snackBar.open(text, 'red-snackbar', 5000);
                this.effectSubject.next('error');
                return of(errorImportAssets());
            })
        )),
    ) });
    

    constructor(
        private _translate: TranslateService,
        private _assetService: AssetService,
        private _actions$: Actions,
        private _snackBar: SnackbarComponent,
        private store: Store,
        private _errorTranslationService: ErrorTranslationService,
        private _cacheService: CacheService,
        private logger: LoggerService,
    ) {
        this.effectSubject = new Subject<EffectResult>();
    }
}
